import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// API Base URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (lang: string) => void;
  t: (key: string, params?: any) => string;
  translateSector: (sectorKey: string) => string;
  translationsLoaded: boolean;
  getCurrentLocale: () => string;
}

// Contexto de idioma con traducciones dinámicas desde backend
export const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: 'es',
  setLanguage: (lang: string) => {},
  t: (key: string, params?: any) => key,
  translateSector: (sectorKey: string) => sectorKey,
  translationsLoaded: false,
  getCurrentLocale: (): string => 'es-ES'
});

// Hook para usar el contexto de idioma
export const useLanguage = () => useContext(LanguageContext);

// Proveedor de contexto de idioma
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState('es');
  const [dynamicTranslations, setDynamicTranslations] = useState<any>({});
  const [translationsLoaded, setTranslationsLoaded] = useState(false);
  
  // Helper to get current locale code for date formatting
  const getCurrentLocale = () => {
    const localeMap: { [key: string]: string } = {
      'es': 'es-ES',
      'en': 'en-US',
      'fr': 'fr-FR',
      'de': 'de-DE',
      'it': 'it-IT',
      'no': 'no-NO',
      'sv': 'sv-SE',
      'nl': 'nl-NL'
    };
    return localeMap[currentLanguage] || 'es-ES';
  };
  
  // Función de traducción que usa solo el backend (con fallback temporal)
  const t = (key: string, params?: any): string => {
    // Primero intentar buscar directamente
    let text = dynamicTranslations[key];
    
    // Si la clave tiene puntos, buscar anidadamente
    if (!text && key.includes('.')) {
      const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
      };
      text = getNestedValue(dynamicTranslations, key);
    }
    
    // Si no se encuentra directamente, buscar en todas las secciones
    if (!text) {
      const sections = ['navigation', 'cv', 'dashboard', 'auth', 'common', 'landing', 'placeholders'];
      for (const sectionName of sections) {
        const section = dynamicTranslations[sectionName];
        if (typeof section === 'object' && section !== null && (section as any)[key]) {
          text = (section as any)[key];
          break;
        }
      }
    }
    
    // Si no está en el backend, devolver la key como fallback
    if (!text) {
      // Solo hacer log en desarrollo para evitar spam
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔍 Traducción no encontrada: ${key}`);
      }
      return key;
    }
    
    // Si text no es string, devolver la key
    if (typeof text !== 'string') {
      return key;
    }
    
    // Reemplazar parámetros en la traducción
    if (params) {
      Object.keys(params).forEach(param => {
        text = (text as string).replace(`{${param}}`, params[param]);
      });
    }
    
    return text;
  };
  
  // Cargar traducciones dinámicas cuando cambie el idioma
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        setTranslationsLoaded(false);
        
        // Intentar cargar traducciones (con o sin token)
        const token = localStorage.getItem('token');
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const translationsResponse = await fetch(`${API_BASE}/api/i18n/${currentLanguage}`, {
          headers
        });
        
        if (translationsResponse.ok) {
          const translationsData = await translationsResponse.json();
          console.log('🔍 DEBUG - Traducciones dinámicas cargadas desde backend:', translationsData);
          // El backend devuelve la estructura anidada: {status, language, translations}
          setDynamicTranslations(translationsData.translations || {});
        } else {
          console.log('Error loading translations - Status:', translationsResponse.status);
        }
      } catch (error) {
        console.log('Error loading translations:', error);
      } finally {
        setTranslationsLoaded(true);
      }
    };

    loadTranslations();
  }, [currentLanguage]);
  
  // Función específica para traducir sectores (ahora también usa backend)
  const translateSector = (sectorKey: string) => {
    return dynamicTranslations?.sectors?.[sectorKey] || sectorKey;
  };
  
  const setLanguage = (lang: string) => {
    setCurrentLanguage(lang);
    localStorage.setItem('appLanguage', lang);
  };
  
  // Cargar idioma guardado al inicializar
  useEffect(() => {
    const savedLang = localStorage.getItem('appLanguage');
    if (savedLang && ['es', 'en', 'fr', 'it', 'de', 'no', 'sv', 'nl'].includes(savedLang)) {
      setCurrentLanguage(savedLang);
    }
  }, []);
  
  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t, translateSector, translationsLoaded, getCurrentLocale }}>
      {children}
    </LanguageContext.Provider>
  );
}
